import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useNavigate } from "react-router-dom";
import { useLanding } from "../context/LandingContext";
import { navbarOffsetSx, resolveNavbarStyle } from "../utils/landingLayout";
import {
  buildWhatsAppUrl,
  fullBusinessAddress,
  handleLandingMediaError,
  resolveLandingMedia,
} from "../utils/landingMedia";
import { openConfiguredUrl } from "../utils/configuredNavigation";
import { safeButtonTextSx, safeTextSx, safeTitleSx, sectionTitleFontSize } from "../utils/textSafety";
import { normalizeImagePosition, scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";

function LandingContactSection({ sectionDomId = "contact" }) {
  const navigate = useNavigate();
  const { contact, business, theme, navigation, sections } = useLanding();
  const navbarOffset = navbarOffsetSx(resolveNavbarStyle(navigation, theme), sections?.showNavbar !== false);
  const whatsappUrl = buildWhatsAppUrl(business?.whatsapp, contact?.whatsappMessage);
  const address = fullBusinessAddress(business);
  const backgroundImage = resolveLandingMedia(contact?.backgroundImageUrl);
  const mapEmbedUrl = String(business?.mapEmbedUrl || "").trim();
  const hasMedia = Boolean(mapEmbedUrl || backgroundImage);
  const backgroundImagePosition = normalizeImagePosition(contact?.backgroundImagePosition);
  const hasDetails = Boolean(address || business?.schedule || business?.phone || business?.email);
  const showWhatsappCta = Boolean(whatsappUrl && contact?.whatsappCtaLabel);
  const showDetailsCta = Boolean(contact?.detailsCtaLabel && contact?.detailsCtaUrl);
  const hasVisibleContent = Boolean(
    contact?.title || contact?.subtitle || hasDetails || showWhatsappCta || showDetailsCta || mapEmbedUrl || backgroundImage
  );
  const backgroundColor = contact?.backgroundColor || "#19242F";
  const decorativeAccentColor = contact?.decorativeAccentColor || "#F35A3A";
  const titleColor = contact?.titleColor || "#FFFFFF";
  const subtitleColor = contact?.subtitleColor || "rgba(255,255,255,.82)";
  const addressTextColor = contact?.addressTextColor || "rgba(255,255,255,.82)";
  const scheduleTextColor = contact?.scheduleTextColor || "rgba(255,255,255,.82)";
  const phoneTextColor = contact?.phoneTextColor || "rgba(255,255,255,.82)";
  const emailTextColor = contact?.emailTextColor || "rgba(255,255,255,.82)";
  const addressIconColor = contact?.addressIconColor || "#F35A3A";
  const scheduleIconColor = contact?.scheduleIconColor || "#F35A3A";
  const phoneIconColor = contact?.phoneIconColor || "#F35A3A";
  const emailIconColor = contact?.emailIconColor || "#F35A3A";
  const surfaceColor = contact?.imageBackgroundColor || "rgba(255,255,255,.08)";
  const detailsCtaTextColor = contact?.detailsCtaStyle?.textColor || "#FFFFFF";
  const titleScale = segmentVisualScale(theme, "contact", "title");
  const imageScale = segmentVisualScale(theme, "contact", "image");
  const spacingScale = segmentVisualScale(theme, "contact", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(contact?.title, { xs: "2.2rem", md: "3.1rem" }),
    titleScale
  );

  if (!hasVisibleContent) return null;

  return (
    <Box
      component="section"
      id={sectionDomId}
      data-landing-section="contact"
      sx={{
        px: { xs: 2, sm: 3, md: 8 },
        py: { xs: 5 * spacingScale, sm: 7 * spacingScale, md: 9 * spacingScale },
        bgcolor: backgroundColor,
        color: titleColor,
        scrollMarginTop: navbarOffset,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.2,
          background: `radial-gradient(circle at 0% 0%, ${decorativeAccentColor} 0%, transparent 42%)`,
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1320,
          mx: "auto",
          display: "grid",
          gridTemplateColumns: hasMedia
            ? {
                xs: "minmax(0, 1.1fr) minmax(130px, .9fr)",
                sm: "minmax(0, 7fr) minmax(280px, 5fr)",
              }
            : "minmax(0, 1fr)",
          gap: { xs: 2, sm: 4, md: 6 },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ minWidth: 0, order: 1 }}>
          <Box sx={{ minWidth: 0, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {contact?.title ? (
              <Typography data-landing-field="contact.title" variant="h3" sx={{ ...safeTitleSx, fontSize: titleSize, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)", color: titleColor }}>
                {capitalizeSectionTitle(contact.title)}
              </Typography>
            ) : null}

            {contact?.subtitle ? (
              <Typography
                data-landing-field="contact.subtitle"
                sx={{
                  ...safeTextSx,
                  mt: contact?.title ? 1.5 : 0,
                  color: subtitleColor,
                  fontSize: { xs: ".98rem", sm: "1.04rem", md: "1.08rem" },
                  lineHeight: { xs: 1.6, md: 1.7 },
                  maxWidth: 760,
                  whiteSpace: "pre-line",
                }}
              >
                {contact.subtitle}
              </Typography>
            ) : null}

            {hasDetails ? (
              <Stack spacing={1.25} sx={{ mt: contact?.title || contact?.subtitle ? 2.5 : 0 }}>
                {address ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <PlaceIcon fontSize="small" sx={{ mt: 0.2, color: addressIconColor }} />
                    <Typography data-landing-field="business.address" sx={{ ...safeTextSx, color: addressTextColor }}>{address}</Typography>
                  </Stack>
                ) : null}

                {business?.schedule ? (
                  <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <ScheduleOutlinedIcon fontSize="small" sx={{ mt: 0.2, color: scheduleIconColor }} />
                    <Typography data-landing-field="business.schedule" sx={{ ...safeTextSx, color: scheduleTextColor }}>{business.schedule}</Typography>
                  </Stack>
                ) : null}

                {business?.phone ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <PhoneOutlinedIcon fontSize="small" sx={{ color: phoneIconColor }} />
                    <Typography
                      data-landing-field="business.phone"
                      component="a"
                      href={`tel:${business.phone}`}
                      sx={{ ...safeTextSx, color: phoneTextColor, textDecoration: "none" }}
                    >
                      {business.phone}
                    </Typography>
                  </Stack>
                ) : null}

                {business?.email ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <EmailOutlinedIcon fontSize="small" sx={{ color: emailIconColor }} />
                    <Typography
                      data-landing-field="business.email"
                      component="a"
                      href={`mailto:${business.email}`}
                      sx={{ ...safeTextSx, color: emailTextColor, textDecoration: "none" }}
                    >
                      {business.email}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            ) : null}

            {showWhatsappCta || showDetailsCta ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: contact?.title || contact?.subtitle || hasDetails ? { xs: 2.5, md: 3.5 } : 0, "& > *": { width: { xs: "100%", sm: "auto" } } }}
              >
              {showWhatsappCta ? (
                <Button
                  data-landing-field="contact.whatsappCtaLabel"
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<WhatsAppIcon />}
                  variant="contained"
                  sx={{
                    ...safeButtonTextSx,
                    maxWidth: "100%",
                    bgcolor: "#25D366",
                    borderRadius: 999,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 900,
                    "&:hover": { bgcolor: "#1fb758" },
                  }}
                >
                  {contact.whatsappCtaLabel}
                </Button>
              ) : null}

              {showDetailsCta ? (
                <Button
                  data-landing-field="contact.detailsCtaLabel"
                  onClick={() => openConfiguredUrl(contact.detailsCtaUrl, navigate)}
                  variant="outlined"
                  sx={{
                    ...safeButtonTextSx,
                    maxWidth: "100%",
                    color: detailsCtaTextColor,
                    borderColor: "rgba(255,255,255,.7)",
                    borderRadius: 999,
                    px: 3,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  {contact.detailsCtaLabel}
                </Button>
              ) : null}
              </Stack>
            ) : null}
          </Box>
        </Box>

        {hasMedia ? (
          <Box sx={{ minWidth: 0, order: 2, display: "flex", alignItems: "stretch" }}>
            {mapEmbedUrl ? (
              <Box
                sx={{
                  width: "100%",
                  minHeight: { xs: 220 * imageScale, sm: 320 * imageScale, md: 360 * imageScale },
                  bgcolor: surfaceColor,
                  overflow: "hidden",
                  borderRadius: 4,
                  border: "1px solid rgba(255,255,255,.18)",
                  boxShadow: "0 20px 55px rgba(0,0,0,.24)",
                }}
              >
                <Box
                  component="iframe"
                  title={contact?.mapTitle || "Ubicación del negocio"}
                  src={mapEmbedUrl}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  sx={{ width: "100%", height: "100%", minHeight: "inherit", border: 0, display: "block" }}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={backgroundImage}
                alt={contact?.mapTitle ?? ""}
                loading="lazy"
                decoding="async"
                onError={handleLandingMediaError}
                sx={{
                  width: "100%",
                  minHeight: { xs: 220 * imageScale, sm: 260 * imageScale, md: 360 * imageScale },
                  maxHeight: { xs: 320 * imageScale, md: 520 * imageScale },
                  bgcolor: surfaceColor,
                  display: "block",
                  objectFit: "cover",
                  objectPosition: backgroundImagePosition,
                  borderRadius: 4,
                  border: "1px solid rgba(255,255,255,.18)",
                  boxShadow: "0 20px 55px rgba(0,0,0,.24)",
                }}
              />
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default LandingContactSection;
